class StickySidebar {
  private static EVENT_KEY = '.stickySidebar';
  private static DEFAULTS = {
    topSpacing: 0,
    bottomSpacing: 0,
    containerSelector: false,
    scrollContainer: false,
    innerWrapperSelector: '.inner-wrapper-sticky',
    stickyClass: 'is-affixed',
    minWidth: false,
  };

  private options: any;
  private sidebar: HTMLElement;
  private sidebarInner: any;
  private container: HTMLElement;
  private affixedType: string;
  private direction: string;
  private support: { transform: boolean; transform3d: boolean };
  private _initialized: boolean;
  private _reStyle: boolean;
  private _breakpoint: boolean;
  private dimensions: any;
  private eventTarget: HTMLElement | Window | undefined;
  private scrollContainer: any;
  private _running: boolean;

  constructor(sidebar: string | HTMLElement, options: any = {}) {
    this.options = StickySidebar.extend(StickySidebar.DEFAULTS, options);
    this.sidebar = typeof sidebar === 'string' ? document.querySelector(sidebar)! : sidebar;

    if (typeof this.sidebar === 'undefined') {
      throw new Error('There is no specific sidebar element.');
    }

    this.sidebarInner = false;
    this.container = this.sidebar.parentElement!;
    this.affixedType = 'STATIC';
    this.direction = 'down';
    this.support = {
      transform: false,
      transform3d: false,
    };

    this._initialized = false;
    this._reStyle = false;
    this._breakpoint = false;

    this.dimensions = {
      translateY: 0,
      maxTranslateY: 0,
      topSpacing: 0,
      lastTopSpacing: 0,
      bottomSpacing: 0,
      lastBottomSpacing: 0,
      sidebarHeight: 0,
      sidebarWidth: 0,
      containerTop: 0,
      containerHeight: 0,
      viewportHeight: 0,
      viewportTop: 0,
      lastViewportTop: 0,
    };

    ['handleEvent'].forEach((method) => {
      this[method] = this[method].bind(this);
    });

    this.initialize();
  }

  private initialize(): void {
    if (this.options.innerWrapperSelector) {
      this.sidebarInner = this.sidebar.querySelector(this.options.innerWrapperSelector);

      if (this.sidebarInner === null) {
        this.sidebarInner = false;
      }
    }

    if (!this.sidebarInner) {
      let wrapper = document.createElement('div');
      wrapper.setAttribute('class', 'inner-wrapper-sticky');
      this.sidebar.appendChild(wrapper);

      while (this.sidebar.firstChild !== wrapper) wrapper.appendChild(this.sidebar.firstChild!);

      this.sidebarInner = this.sidebar.querySelector('.inner-wrapper-sticky');
    }

    if (this.options.containerSelector) {
      let containers = document.querySelectorAll(this.options.containerSelector);
      containers = Array.prototype.slice.call(containers);

      containers.forEach((container, item) => {
        if (!container.contains(this.sidebar)) return;
        this.container = container;
      });

      if (!containers.length) {
        throw new Error('The container does not contain the sidebar.');
      }
    }

    this.scrollContainer = this.options.scrollContainer ? document.querySelector(this.options.scrollContainer) : undefined;

    if (typeof this.options.topSpacing !== 'function') {
      this.options.topSpacing = parseInt(this.options.topSpacing) || 0;
    }

    if (typeof this.options.bottomSpacing !== 'function') {
      this.options.bottomSpacing = parseInt(this.options.bottomSpacing) || 0;
    }

    this._widthBreakpoint();
    this.calcDimensions();
    this.stickyPosition();
    this.bindEvents();
    this._initialized = true;
  }

  private bindEvents(): void {
    this.eventTarget = this.scrollContainer ? this.scrollContainer : window;

    this.eventTarget!.addEventListener('resize', this as any, { passive: true, capture: false });
    this.eventTarget!.addEventListener('scroll', this as any, { passive: true, capture: false });

    this.sidebar.addEventListener('update' + StickySidebar.EVENT_KEY, this as any);

    if ('undefined' !== typeof ResizeObserver) {
      const resizeObserver = new ResizeObserver(() => this.handleEvent());
      resizeObserver.observe(this.sidebarInner);
      resizeObserver.observe(this.container);
    }
  }

  private handleEvent(event = {} as Event): void {
    this.updateSticky(event);
  }

  private calcDimensions(): void {
    if (this._breakpoint) return;
    var dims = this.dimensions;

    dims.containerTop = StickySidebar.offsetRelative(this.container).top;
    dims.containerHeight = this.container.clientHeight;
    dims.containerBottom = dims.containerTop + dims.containerHeight;

    dims.sidebarHeight = this.sidebarInner.offsetHeight;
    dims.sidebarWidth = this.sidebarInner.offsetWidth;

    dims.viewportHeight = window.innerHeight;

    dims.maxTranslateY = dims.containerHeight - dims.sidebarHeight;

    this._calcDimensionsWithScroll();
  }

  private _calcDimensionsWithScroll(): void {
    var dims = this.dimensions;

    dims.sidebarLeft = StickySidebar.offsetRelative(this.sidebar).left;

    if (this.scrollContainer) {
      dims.viewportTop = this.scrollContainer.scrollTop;
      dims.viewportLeft = this.scrollContainer.scrollLeft;
    } else {
      dims.viewportTop = document.documentElement.scrollTop || document.body.scrollTop;
      dims.viewportLeft = document.documentElement.scrollLeft || document.body.scrollLeft;
    }
    dims.viewportBottom = dims.viewportTop + dims.viewportHeight;

    dims.topSpacing = this.options.topSpacing;
    dims.bottomSpacing = this.options.bottomSpacing;

    if (typeof dims.topSpacing === 'function') dims.topSpacing = parseInt(dims.topSpacing(this.sidebar)) || 0;

    if (typeof dims.bottomSpacing === 'function') dims.bottomSpacing = parseInt(dims.bottomSpacing(this.sidebar)) || 0;

    if ('VIEWPORT-TOP' === this.affixedType) {
      if (dims.topSpacing < dims.lastTopSpacing) {
        dims.translateY += dims.lastTopSpacing - dims.topSpacing;
        this._reStyle = true;
      }
    } else if ('VIEWPORT-BOTTOM' === this.affixedType) {
      if (dims.bottomSpacing < dims.lastBottomSpacing) {
        dims.translateY += dims.lastBottomSpacing - dims.bottomSpacing;
        this._reStyle = true;
      }
    }

    dims.lastTopSpacing = dims.topSpacing;
    dims.lastBottomSpacing = dims.bottomSpacing;
  }

  private isSidebarFitsViewport(): boolean {
    return this.dimensions.viewportHeight >=
      this.dimensions.lastBottomSpacing + this.dimensions.lastTopSpacing + this.dimensions.sidebarHeight;
  }

  private observeScrollDir(): void {
    var dims = this.dimensions;
    if (dims.lastViewportTop === dims.viewportTop) return;

    var furthest = 'down' === this.direction ? Math.min : Math.max;

    if (dims.viewportTop === furthest(dims.viewportTop, dims.lastViewportTop))
      this.direction = 'down' === this.direction ? 'up' : 'down';
  }

  private getAffixType(): string {
    this._calcDimensionsWithScroll();
    var dims = this.dimensions;
    var colliderTop = dims.viewportTop + dims.topSpacing;
    var affixType = this.affixedType;

    if (colliderTop <= dims.containerTop || dims.containerHeight <= dims.sidebarHeight) {
      dims.translateY = 0;
      affixType = 'STATIC';
    } else {
      affixType = 'up' === this.direction ? this._getAffixTypeScrollingUp() : this._getAffixTypeScrollingDown();
    }

    dims.translateY = Math.max(0, dims.translateY);
    dims.translateY = Math.min(dims.containerHeight, dims.translateY);
    dims.translateY = Math.round(dims.translateY);

    dims.lastViewportTop = dims.viewportTop;
    return affixType;
  }

  private _getAffixTypeScrollingDown(): string {
    var dims = this.dimensions;
    var sidebarBottom = dims.sidebarHeight + dims.containerTop;
    var colliderTop = dims.viewportTop + dims.topSpacing;
    var colliderBottom = dims.viewportBottom - dims.bottomSpacing;
    var affixType = this.affixedType;

    if (this.isSidebarFitsViewport()) {
      if (dims.sidebarHeight + colliderTop >= dims.containerBottom) {
        dims.translateY = dims.containerBottom - sidebarBottom;
        affixType = 'CONTAINER-BOTTOM';
      } else if (colliderTop >= dims.containerTop) {
        dims.translateY = colliderTop - dims.containerTop;
        affixType = 'VIEWPORT-TOP';
      }
    } else {
      if (dims.containerBottom <= colliderBottom) {
        dims.translateY = dims.containerBottom - sidebarBottom;
        affixType = 'CONTAINER-BOTTOM';
      } else if (sidebarBottom + dims.translateY <= colliderBottom) {
        dims.translateY = colliderBottom - sidebarBottom;
        affixType = 'VIEWPORT-BOTTOM';
      } else if (
        dims.containerTop + dims.translateY <= colliderTop &&
        (0 !== dims.translateY && dims.maxTranslateY !== dims.translateY)
      ) {
        affixType = 'VIEWPORT-UNBOTTOM';
      }
    }

    return affixType;
  }

  private _getAffixTypeScrollingUp(): string {
    var dims = this.dimensions;
    var sidebarBottom = dims.sidebarHeight + dims.containerTop;
    var colliderTop = dims.viewportTop + dims.topSpacing;
    var colliderBottom = dims.viewportBottom - dims.bottomSpacing;
    var affixType = this.affixedType;

    if (colliderTop <= dims.translateY + dims.containerTop) {
      dims.translateY = colliderTop - dims.containerTop;
      affixType = 'VIEWPORT-TOP';
    } else if (dims.containerBottom <= colliderBottom) {
      dims.translateY = dims.containerBottom - sidebarBottom;
      affixType = 'CONTAINER-BOTTOM';
    } else if (!this.isSidebarFitsViewport()) {
      if (dims.containerTop <= colliderTop && (0 !== dims.translateY && dims.maxTranslateY !== dims.translateY)) {
        affixType = 'VIEWPORT-UNBOTTOM';
      }
    }

    return affixType;
  }

  private _getStyle(affixType: string): { inner: any; outer: any } | undefined {
    if (typeof affixType === 'undefined') return;

    var style = { inner: {}, outer: {} };
    var dims = this.dimensions;

    switch (affixType) {
      case 'VIEWPORT-TOP':
        style.inner = {
          position: 'fixed',
          top: dims.topSpacing,
          left: dims.sidebarLeft - dims.viewportLeft,
          width: dims.sidebarWidth,
        };
        break;
      case 'VIEWPORT-BOTTOM':
        style.inner = {
          position: 'fixed',
          top: 'auto',
          left: dims.sidebarLeft,
          bottom: dims.bottomSpacing,
          width: dims.sidebarWidth,
        };
        break;
      case 'CONTAINER-BOTTOM':
      case 'VIEWPORT-UNBOTTOM':
        let translate = this._getTranslate(0, dims.translateY, 0);

        if (translate) style.inner = { transform: translate };
        else style.inner = { position: 'absolute', top: dims.translateY, width: dims.sidebarWidth };
        break;
    }

    switch (affixType) {
      case 'VIEWPORT-TOP':
      case 'VIEWPORT-BOTTOM':
      case 'VIEWPORT-UNBOTTOM':
      case 'CONTAINER-BOTTOM':
        style.outer = { height: dims.sidebarHeight, position: 'relative' };
        break;
    }

    style.outer = StickySidebar.extend({ height: '', position: '' }, style.outer);
    style.inner = StickySidebar.extend(
      { position: 'relative', top: '', left: '', bottom: '', width: '', transform: '' },
      style.inner
    );

    return style;
  }

  private stickyPosition(force?: boolean): void {
    if (this._breakpoint) return;

    force = this._reStyle || force || false;

    var affixType = this.getAffixType();
    var style = this._getStyle(affixType)!;

    if ((this.affixedType != affixType || force) && affixType) {
      let affixEvent = 'affix.' + affixType.toLowerCase().replace('viewport-', '') + StickySidebar.EVENT_KEY;
      StickySidebar.eventTrigger(this.sidebar, affixEvent);

      if ('STATIC' === affixType) StickySidebar.removeClass(this.sidebar, this.options.stickyClass);
      else StickySidebar.addClass(this.sidebar, this.options.stickyClass);

      for (let key in style.outer) {
        let unit = typeof style.outer[key] === 'number' ? 'px' : '';
        this.sidebar.style[key] = style.outer[key] + unit;
      }

      for (let key in style.inner) {
        let unit = typeof style.inner[key] === 'number' ? 'px' : '';
        this.sidebarInner.style[key] = style.inner[key] + unit;
      }

      let affixedEvent = 'affixed.' + affixType.toLowerCase().replace('viewport-', '') + StickySidebar.EVENT_KEY;
      StickySidebar.eventTrigger(this.sidebar, affixedEvent);
    } else {
      if (this._initialized) this.sidebarInner.style.left = style.inner.left;
    }

    this.affixedType = affixType;
  }

  private _widthBreakpoint(): void {
    if (window.innerWidth <= this.options.minWidth) {
      this._breakpoint = true;
      this.affixedType = 'STATIC';

      this.sidebar.removeAttribute('style');
      StickySidebar.removeClass(this.sidebar, this.options.stickyClass);
      this.sidebarInner.removeAttribute('style');
    } else {
      this._breakpoint = false;
    }
  }

  private updateSticky(event = {} as Event): void {
    if (this._running) return;
    this._running = true;

    ((eventType) => {
      requestAnimationFrame(() => {
        switch (eventType) {
          case 'scroll':
            this._calcDimensionsWithScroll();
            this.observeScrollDir();
            this.stickyPosition();
            break;
          case 'resize':
          default:
            this._widthBreakpoint();
            this.calcDimensions();
            this.stickyPosition(true);
            break;
        }
        this._running = false;
      });
    })(event.type);
  }

  private _getTranslate(y: number = 0, x: number = 0, z: number = 0): string | false {
    if (this.support.transform3d) return 'translate3d(' + y + ', ' + x + ', ' + z + ')';
    else if (this.support.transform) return 'translate(' + y + ', ' + x + ')';
    else return false;
  }

  destroy(): void {
    window.removeEventListener('resize', this as any, { capture: false });
    window.removeEventListener('scroll', this as any, { capture: false });

    this.sidebar.classList.remove(this.options.stickyClass);
    this.sidebar.style.minHeight = '';

    this.sidebar.removeEventListener('update' + StickySidebar.EVENT_KEY, this as any);

    var styleReset = { inner: {}, outer: {} };

    styleReset.inner = {
      position: '',
      top: '',
      left: '',
      bottom: '',
      width: '',
      transform: '',
    };
    styleReset.outer = { height: '', position: '' };

    for (let key in styleReset.outer) this.sidebar.style[key] = styleReset.outer[key];

    for (let key in styleReset.inner) this.sidebarInner.style[key] = styleReset.inner[key];
  }

  static eventTrigger(element: Element, eventName: string, data?: any): void {
    try {
      var event = new CustomEvent(eventName, { detail: data });
    } catch (e) {
      var event = document.createEvent('CustomEvent');
      event.initCustomEvent(eventName, true, true, data);
    }
    element.dispatchEvent(event);
  }

  static extend(defaults: { [key: string]: any }, options: { [key: string]: any }): { [key: string]: any } {
    var results = {};
    for (let key in defaults) {
      if (typeof options[key] !== 'undefined') results[key] = options[key];
      else results[key] = defaults[key];
    }
    return results;
  }

  static offsetRelative(element: HTMLElement): { left: number; top: number } {
    var result = { left: 0, top: 0 };

    do {
      let offsetTop = element.offsetTop;
      let offsetLeft = element.offsetLeft;

      if (!isNaN(offsetTop)) result.top += offsetTop;

      if (!isNaN(offsetLeft)) result.left += offsetLeft;

      element = 'BODY' === element.tagName ? (element as any).parentElement : element.offsetParent;
    } while (element);
    return result;
  }

  static addClass(element: HTMLElement, className: string): void {
    if (!StickySidebar.hasClass(element, className)) {
      if ((element as any).classList) (element as any).classList.add(className);
      else (element as any).className += ' ' + className;
    }
  }

  static removeClass(element: HTMLElement, className: string): void {
    if (StickySidebar.hasClass(element, className)) {
      if ((element as any).classList) (element as any).classList.remove(className);
      else
        (element as any).className = (element as any).className.replace(
          new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'),
          ' '
        );
    }
  }

  static hasClass(element: HTMLElement, className: string): boolean {
    if ((element as any).classList) return (element as any).classList.contains(className);
    else return new RegExp('(^| )' + className + '( |$)', 'gi').test((element as any).className);
  }

  static get defaults(): { [key: string]: any } {
    return StickySidebar.DEFAULTS;
  }
}

export default StickySidebar;
